import type { Cycle } from "@src/packages/care/components/frozen-storage-data";
import { semenSampleIdentity } from "./specimenRetryIdentity";

type SampleIdentityCycle = Pick<Cycle, "id" | "type" | "specimenIds">;

/**
 * Resolve one stable sample identity per semen collection represented by a
 * specimen-intake batch.
 *
 * Existing specimen rows without a known sample ID are left untouched. The
 * client cannot safely infer whether those rows came from the same collection
 * as one another, so callers should wait for an explicit API identity before
 * projecting them as a shared sample.
 */
export function resolveSemenSampleIds(
  cycles: readonly SampleIdentityCycle[],
  providedSampleIds: Record<string, string> | undefined,
  createSampleId: () => string,
): Record<string, string> {
  const sampleIds = { ...providedSampleIds };

  for (const cycle of cycles) {
    // Keyed by the cycle's stable form id (#2431): removing or reordering a
    // sibling cycle card between retries must not re-map sample identities.
    const key = semenSampleIdentity(cycle);
    if (cycle.type !== "sperm" || sampleIds[key]) continue;
    if ((cycle.specimenIds?.length ?? 0) === 0) {
      sampleIds[key] = createSampleId();
    }
  }

  return sampleIds;
}
